import type { ReactNode } from 'react';

import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { StarIcon } from '../../images/shapes';

import Loader from '../Loader/Loader';

export type PinContentFn = (elementId: string, pined: boolean) => Promise<void>;

export interface PinContentProps {
  /**
   * If content is pinned
   */
  pined?: boolean;
  /**
   * Parent folder path
   */
  parent?: string;
  /**
   * Title of the content
   */
  title?: ReactNode;
  /**
   * Callback to pin or unpin content
   */
  pinContentCallback?: PinContentFn;
  /**
   * Element ID to pin or unpin
   */
  elementId?: string;
  /**
   * Additional classes for the container
   */
  additionalContainerClasses?: string;
  /**
   * Test id for the component
   */
  testId?: string;
}

export const PinContent = ({
  pined = false,
  parent = '',
  title = '',
  pinContentCallback,
  elementId = '',
  additionalContainerClasses,
  testId,
}: PinContentProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const elementLabel = useMemo(() => {
    if (!parent) return title;
    const folderName = parent.split('/')?.[1];
    const inFavorite =
      typeof title === 'string' && title.split('/').length !== 1;
    return folderName && !inFavorite ? `${folderName}/${title}` : title;
  }, [parent, title]);

  if (!parent) return;

  return (
    <button
      type="button"
      className={twMerge(
        additionalContainerClasses,
        'absolute flex justify-center left-1',
      )}
      onClick={async (event) => {
        event.stopPropagation();
        event.preventDefault();

        setIsLoading(true);
        await pinContentCallback?.(elementId, pined);
        setIsLoading(false);
      }}
      {...getTestProps(testId, `pin-content-${elementLabel}`)}
    >
      <Loader
        additionalClasses={twMerge(
          'h-4 w-4 transition-opacity duration-700! invisible opacity-0 dark:text-white dark:invert',
          isLoading && 'visible opacity-100 ml-[18px]',
        )}
      />
      <StarIcon
        {...getTestProps(testId, `pin-content-icon-${elementLabel}`)}
        className={twMerge(
          'h-5 cursor-pointer hidden',
          !isLoading &&
            (pined
              ? 'block fill-orange-400'
              : 'group-hover:fill-slate-500 group-hover:block!'),
        )}
      />
    </button>
  );
};

export default PinContent;
